/**
 * Tool Error Detection
 *
 * Shared utility for detecting soft errors returned by tool handlers.
 * Extracted into its own module so it can be tested without pulling
 * in the full agent-tools dependency tree.
 */

/**
 * Error prefixes returned by tool handlers for domain-level failures.
 * Kept as an array so tests can verify coverage against actual tool returns.
 */
export const TOOL_ERROR_PREFIXES = [
    'Error:',
    'Build error:',
    'Compilation error:',
    'Runtime error',
    'Validation error:',
    'Materialization error:',
    'Unknown tool:',
];

/** A leading "Error " followed by a verb phrase and a colon, e.g. "Error generating image:". */
const ERROR_SENTENCE = /^Error [a-z][^:\n]{0,80}:/;

/**
 * Check whether a tool result string represents a soft error.
 * Tool handlers return error strings (rather than throwing) for
 * domain-level failures like "object not found" or "compilation error".
 */
export function isToolErrorResult(result: string): boolean {
    return TOOL_ERROR_PREFIXES.some(prefix => result.startsWith(prefix))
        // "Error generating 3D model: …", "Error adding behavior: …" — the
        // verb-phrase family. Before this they read as successes: a ✓ chip
        // over a failed 3D generation, and a `result` (not `error`) payload
        // to the coordinator.
        || ERROR_SENTENCE.test(result);
}
